import { useEffect, useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { ArrowUpRight, Trash2, LogOut, PenLine } from "lucide-react";
import { Reveal } from "@/components/Reveal";
import { API } from "@/siteConfig";

const TOKEN_KEY = "qu_admin_token";

export default function Admin() {
  const [token, setToken] = useState(() => localStorage.getItem(TOKEN_KEY) || "");
  const [password, setPassword] = useState("");
  const [posts, setPosts] = useState([]);
  const [form, setForm] = useState({ title: "", category: "", excerpt: "", body_text: "" });
  const [publishing, setPublishing] = useState(false);

  const authHeaders = { headers: { Authorization: `Bearer ${token}` } };

  const loadPosts = () => {
    axios
      .get(`${API}/blog/posts`)
      .then((res) => setPosts(res.data))
      .catch(() => setPosts([]));
  };

  useEffect(() => {
    if (token) loadPosts();
  }, [token]);

  const login = async (e) => {
    e.preventDefault();
    try {
      const res = await axios.post(`${API}/admin/login`, { password });
      localStorage.setItem(TOKEN_KEY, res.data.token);
      setToken(res.data.token);
      toast.success("Welcome back, Karrie.");
    } catch {
      toast.error("Incorrect password.");
    }
  };

  const logout = () => {
    localStorage.removeItem(TOKEN_KEY);
    setToken("");
  };

  const publish = async (e) => {
    e.preventDefault();
    setPublishing(true);
    try {
      await axios.post(`${API}/blog/posts`, form, authHeaders);
      toast.success("Article published.");
      setForm({ title: "", category: "", excerpt: "", body_text: "" });
      loadPosts();
    } catch (err) {
      if (err.response?.status === 401) {
        toast.error("Session expired — please sign in again.");
        logout();
      } else {
        toast.error("Could not publish. Please try again.");
      }
    } finally {
      setPublishing(false);
    }
  };

  const remove = async (post) => {
    try {
      await axios.delete(`${API}/blog/posts/${post.id}`, authHeaders);
      toast.success(`"${post.title}" deleted.`);
      loadPosts();
    } catch {
      toast.error("Could not delete. Please try again.");
    }
  };

  if (!token) {
    return (
      <main className="flex min-h-screen items-center justify-center px-6" data-testid="admin-login-page">
        <Reveal className="w-full max-w-md">
          <p className="overline-label mb-4">Owner Access</p>
          <h1 className="font-serif text-3xl text-white sm:text-4xl">Insights Publisher</h1>
          <p className="mt-4 font-sans text-sm text-white/50">
            Sign in to write and publish new articles.
          </p>
          <form onSubmit={login} className="mt-10 space-y-8" data-testid="admin-login-form">
            <div>
              <label className="field-label" htmlFor="admin-password">Password</label>
              <input
                id="admin-password"
                type="password"
                required
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                className="field-input"
                placeholder="Your admin password"
                data-testid="admin-password-input"
              />
            </div>
            <button type="submit" className="btn-primary" data-testid="admin-login-submit">
              Sign In
              <ArrowUpRight className="h-4 w-4" />
            </button>
          </form>
        </Reveal>
      </main>
    );
  }

  return (
    <main className="mx-auto max-w-5xl px-6 pt-40 pb-24 md:px-10 md:pt-48" data-testid="admin-page">
      <div className="flex flex-wrap items-center justify-between gap-6">
        <div>
          <p className="overline-label mb-4">Owner Access</p>
          <h1 className="font-serif text-3xl text-white sm:text-4xl">Insights Publisher</h1>
        </div>
        <button onClick={logout} className="btn-ghost !px-5 !py-2.5" data-testid="admin-logout">
          <LogOut className="h-4 w-4" /> Sign out
        </button>
      </div>

      {/* New article */}
      <section className="mt-16" data-testid="admin-editor">
        <p className="overline-label mb-8 flex items-center gap-2">
          <PenLine className="h-4 w-4" /> New Article
        </p>
        <form onSubmit={publish} className="space-y-8" data-testid="admin-publish-form">
          <div className="grid gap-8 sm:grid-cols-2">
            <div>
              <label className="field-label" htmlFor="post-title">Title *</label>
              <input
                id="post-title"
                required
                value={form.title}
                onChange={(e) => setForm({ ...form, title: e.target.value })}
                className="field-input"
                placeholder="Article title"
                data-testid="admin-field-title"
              />
            </div>
            <div>
              <label className="field-label" htmlFor="post-category">Category</label>
              <input
                id="post-category"
                value={form.category}
                onChange={(e) => setForm({ ...form, category: e.target.value })}
                className="field-input"
                placeholder="e.g., Leadership, AI Transformation"
                data-testid="admin-field-category"
              />
            </div>
          </div>
          <div>
            <label className="field-label" htmlFor="post-excerpt">Excerpt *</label>
            <input
              id="post-excerpt"
              required
              value={form.excerpt}
              onChange={(e) => setForm({ ...form, excerpt: e.target.value })}
              className="field-input"
              placeholder="One or two sentences shown on the Insights page"
              data-testid="admin-field-excerpt"
            />
          </div>
          <div>
            <label className="field-label" htmlFor="post-body">Article *</label>
            <textarea
              id="post-body"
              required
              rows={12}
              value={form.body_text}
              onChange={(e) => setForm({ ...form, body_text: e.target.value })}
              className="field-input resize-y"
              placeholder={"Write your article here.\n\nSeparate paragraphs with a blank line.\n\n## Start a line with ## for a heading"}
              data-testid="admin-field-body"
            />
          </div>
          <button
            type="submit"
            disabled={publishing}
            className="btn-primary disabled:cursor-not-allowed disabled:opacity-50"
            data-testid="admin-publish-submit"
          >
            {publishing ? "Publishing…" : "Publish Article"}
            {!publishing && <ArrowUpRight className="h-4 w-4" />}
          </button>
        </form>
      </section>

      {/* Existing articles */}
      <section className="mt-24" data-testid="admin-posts">
        <p className="overline-label mb-8">Published Articles</p>
        <div className="divide-y divide-white/10 border-y border-white/10">
          {posts.length === 0 && (
            <p className="py-8 font-sans text-sm text-white/40">No articles yet.</p>
          )}
          {posts.map((p) => (
            <div key={p.id} className="flex items-center justify-between gap-6 py-5" data-testid={`admin-post-${p.slug}`}>
              <div>
                <p className="font-serif text-lg text-white">{p.title}</p>
                <p className="mt-1 font-sans text-xs text-white/40">
                  {p.category} · {p.date} · {p.read_time}
                </p>
              </div>
              <button
                onClick={() => remove(p)}
                className="text-white/40 transition-colors duration-300 hover:text-red-400"
                aria-label={`Delete ${p.title}`}
                data-testid={`admin-delete-${p.slug}`}
              >
                <Trash2 className="h-4 w-4" />
              </button>
            </div>
          ))}
        </div>
      </section>
    </main>
  );
}
